import BackTopbar, { TopbarClearfix } from '@elements/BackTopbar'
import styled from '@emotion/styled'
import { colors, fontSize, size } from '@styles/theme'
import axios from '@utils/webRequests'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { ActionButton, ActionButtonContainer } from './AccountElements'
import { PiArrowRightBold, PiProhibitBold } from 'react-icons/pi'
import useYesOrNo from '@hooks/useYesOrNo'
import YesOrNo from '@elements/YesOrNo'
import useLoading from '@hooks/useLoading'
import useSnackBarContext from '@contexts/snackBarContext'
import UpdatePaymentMethodModal from './UpdatePaymentMethodModal'
import useDialog from '@hooks/useDialog'

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center

  height: 100%;
  width: 100%;
`

const DataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center

  width: 100%;
`

const DataItem = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 0.5px solid rgba(255,255,255, 0.2);
  height: ${size(10)};
  padding: 0 ${size(2)};
`

const DataItemLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  flex-grow: 1;
  font-weight: 600;
`
  
const DataItemValue = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
  height: 100%;
  font-size: ${fontSize('sm')};
  font-weight: 300;
`

const ValueContainer = styled.div`
  padding: ${size(1)} ${size(1.5)};
  border-radius: 100px;
  background-color: ${colors.statusColors.right_now('primary')}75;
  border: 0.75px solid ${colors.statusColors.right_now('primary')};
`

const AccountManageSubscription = () => {
  const { snackError } = useSnackBarContext()
  const { isLoading: isCancelLoading, setIsLoading: setIsCancelLoading, isLoadingDelayed: isCancelLoadingDelayed } = useLoading()
  const [isUpdateOpen, openUpdate, closeUpdate] = useDialog('update_payment_method_modal', 'update-payment-method-modal-open')
  const [isCancelOpen, openCancel, closeCancel] = useYesOrNo('subscription-management-cancel-subscription')
  const [subscription, setSubscription] = useState(null)

  useEffect(() => {
    axios.get('/api/billing/customers')
      .then((res) => setSubscription(res.data))
      .catch(console.error)
  }, [])

  const cancelSubscription = () => {
    setIsCancelLoading(true)
    return axios.put('/api/billing/cancel_subscriptions')
      .then((res) => {
        setIsCancelLoading(false)
        setSubscription({ ...subscription, cancel_at_period_end: true })
      })
      .catch((error) => {
        setIsCancelLoading(false)
        snackError({ error, fallbackMessage: 'Failed to cancel. Please try again or contact support.' })
      })
  }

  const handleCancelSubscriptionSelect = async (yesOrNo) => {
    if(yesOrNo) await cancelSubscription()
    closeCancel()
  }

  const renderDataDisplay = () => (
    <DataContainer>
      {subscription.cancel_at_period_end &&
        <DataItem>
          <DataItemLabel>
            Expires at Period End:
          </DataItemLabel>
          <DataItemValue>
            <ValueContainer>
              YES
            </ValueContainer>
          </DataItemValue>
        </DataItem>
      }
      <DataItem>
        <DataItemLabel>
          Period End:
        </DataItemLabel>
        <DataItemValue>
          <ValueContainer>
            {subscription.current_period_end && format(new Date(subscription.current_period_end), 'MMM d, yyyy')}
          </ValueContainer>
        </DataItemValue>
      </DataItem>
      <DataItem>
        <DataItemLabel>
          Status:
        </DataItemLabel>
        <DataItemValue>
          <ValueContainer>
            {subscription.is_subscribed ? 'ACTIVE' : 'INACTIVE'}
          </ValueContainer>
        </DataItemValue>
      </DataItem>
    </DataContainer>
  )

  const renderLoadingMessage = () => (
    <h3 style={{ textAlign: 'center', marginTop: size(4) }}>Retrieving information...</h3>
  )

  const renderSubscriptionManagement = () => (
    <SectionContainer>
      <YesOrNo
        isOpen={isCancelOpen}
        setClosed={closeCancel}
        handleSelect={handleCancelSubscriptionSelect}
        isLoading={isCancelLoading}
        isLoadingDelayed={isCancelLoadingDelayed}
        title='Cancel Subscription'
        body='Your subscription will remain active until the end of your current payment period.'
      />
      {isUpdateOpen &&
        <UpdatePaymentMethodModal
          isOpen={isUpdateOpen}
          setClosed={closeUpdate}
        />
      }
      {renderDataDisplay()}
      <h3 style={{ textAlign: 'left', padding: size(1.5), paddingLeft: size(3) }}>Account Actions</h3>
      <ActionButtonContainer style={{ padding: `0 ${size(2)}`, gap: size(2)  }}>
        <ActionButton
          onClick={() => openUpdate()}
          style={{ backgroundColor: colors.statusColors.chatting('lightRgba')(0.3), width: '100%' }}>
          Update Payment Method&nbsp;<PiArrowRightBold />
        </ActionButton>
        <ActionButton
          onClick={() => openCancel()}
          style={{ backgroundColor: colors.statusColors.browsing('lightRgba')(0.3), width: '100%' }}>
          <PiProhibitBold />&nbsp;Cancel Subscription
        </ActionButton>
      </ActionButtonContainer>
    </SectionContainer>
  )

  const renderCancelledSubscription = () => (
    <SectionContainer>
      {renderDataDisplay()}
      <h3 style={{ textAlign: 'center', padding: size(2), lineHeight: size(4) }}>
        Your subscription is set to expire on <b style={{ color: colors.statusColors.right_now('primary'), whiteSpace: 'nowrap' }}>{format(new Date(subscription.current_period_end), 'MMMM d, yyyy')}</b>.
        You may add a new subscription any time after.
      </h3>
    </SectionContainer>
  )

  const renderContents = () => {
    if (!subscription) return renderLoadingMessage()
    else if (subscription.cancel_at_period_end) return renderCancelledSubscription()
    else if (subscription.is_subscribed) return renderSubscriptionManagement()
  }

  return (
    <Container>
      <BackTopbar title='Your Subscription' />
      <TopbarClearfix style={{ height: size(7) }} />
      {renderContents()}
    </Container>
  )
}

export default AccountManageSubscription
